(ns app.front.view.admin.oidc-configs
  (:require [app.domain.group.model :as group-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.admin.common :as admin-common]
            [clojure.string :as str]
            [core.authorization.oidc.model :as oidc-model]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema-tools.core :as st]
            [schema.core :as s]))


(def mandatory-keys [:oidc-config/id
                     :oidc-config/display-name
                     :oidc-config/client-id
                     :oidc-config/client-secret])

(def config-keys [:oidc-config/issuer
                  :oidc-config/jwks-uri
                  :oidc-config/authorization-endpoint
                  :oidc-config/token-endpoint
                  :oidc-config/userinfo-endpoint
                  :oidc-config/revocation-endpoint])


(def default-config {::new-config?                      true
                     :oidc-config/id                    "new-config"
                     :oidc-config/display-name          "New configuration"
                     :oidc-config/profile-scope?        false
                     :oidc-config/check-email-verified? true
                     :oidc-config/skip-iss-validation?  false
                     :oidc-config/public-login?         false})


(rf/reg-event-fx ::fetch-domain-groups
  (fn [_ _]
    {:comm/command {:command    :admin/domain-groups-with-domain-name
                    :method     :get
                    :on-success [::entities/merge-entity-db]}}))


(rf/reg-sub ::domain-groups
  :<- [::entities/entities (filter #(and (group-model/domain? %)
                                         (:group/domain %)))]
  (fn [groups]
    (->> groups
         (sort-by :group/name))))


(rf/reg-event-fx ::fetch-oidc-configs
  (fn [_ _]
    {:comm/command {:command    :admin/oidc-configs
                    :method     :get
                    :on-success [::entities/merge-entity-db]}}))


(rf/reg-event-fx ::save-config
  (fn [{:keys [db]} [_ config]]
    {:db           (assoc-in db [::data ::saving?] true)
     :comm/command {:command          :admin/save-oidc-config
                    :method           :post
                    :data             (->> (dissoc config ::new-config?)
                                           (p/filter-vals #(or (not (string? %))
                                                               (not (str/blank? %)))))
                    :on-success       [::save-config.success]
                    :finally-dispatch [::stop-saving]}}))


(rf/reg-event-fx ::save-config.success
  (fn [{:keys [db]} [_ status response]]
    {:db         (assoc-in db [::data ::saving?] false)
     :dispatch-n [[::entities/apply-changes status response]
                  [::set-config-in-editor nil]
                  [:notifications/add :toast :success :success :admin.oidc-configs/saved]]}))


(rf/reg-event-fx ::delete-config
  (fn [_ [_ id]]
    {:comm/command {:command          :admin/delete-oidc-config
                    :method           :post
                    :data             {:db/id id}
                    :on-success       [::delete-config.success id]
                    :finally-dispatch [::stop-saving]}}))


(rf/reg-event-fx ::delete-config.success
  (fn [_ [_ id]]
    {:dispatch-n [[::entities/remove-entity id]
                  [::set-config-in-editor nil]
                  [:notifications/add :toast :success :success :admin.oidc-configs/deleted]]}))


(rf/reg-event-db ::stop-saving
  #(assoc-in % [::data ::saving?] false))



(rf/reg-event-db ::set-config-in-editor
  (fn [db [_ config]]
    (assoc-in db
              [::data ::config-in-editor]
              ; De-entityize
              (-> (select-keys config (concat (keys (st/required-keys oidc-model/OIDCConfigKeys))
                                              (keys default-config)
                                              [:db/id]))
                  (update :oidc-config/domain-groups (comp set (partial map :db/id)))))))


(rf/reg-event-db ::set-editor-value
  (fn [db [_ k v]]
    (if (or (nil? v) (and (string? v) (str/blank? v)))
      (p/dissoc-in db [::data ::config-in-editor k])
      (assoc-in db [::data ::config-in-editor k] v))))


(rf/reg-event-db ::toggle-domain-group
  (fn [db [_ add? v]]
    (update-in db
               [::data ::config-in-editor :oidc-config/domain-groups]
               (comp set (if add? conj disj))
               v)))


(rf/reg-sub ::config-in-editor
  #(-> % ::data ::config-in-editor))


(rf/reg-sub ::save-in-progress?
  #(-> % ::data ::saving?))


(defn format-group-name [{:group/keys [name domain]}]
  (str name " (" domain ")"))


(defn callback-uri [tr {:oidc-config/keys [id]}]
  [:div.mb-3
   [:span.mr-2 (tr [:admin.oidc-configs/redirect-uri])]
   [:span.bold (str (.-protocol js/window.location) "//"
                    (.-host js/window.location)
                    "/api/oidc/" id "/sign-in")]])


(defn labeled-text [label text]
  [:div
   [:div.bx--label label]
   [:div.label-value text]])


(defn field [tr editing? disabled? k config config-in-editor]
  [:div.mt-3
   (if editing?
     [ui/text-input {:id          (name k)
                     :label-text  (tr [k])
                     :value       (get config-in-editor k)
                     :disabled    disabled?
                     :on-change   #(rf/dispatch-sync [::set-editor-value k (futil/tval %)])
                     :helper-text (when (oidc-model/secret-fields k)
                                    (tr [:gis-server-config/password-hidden]))}]
     [labeled-text (tr [k]) (or (get config k)
                                (tr [(if (oidc-model/secret-fields k)
                                       :gis-server-config/password-hidden
                                       :common/not-set)]))])])


(defn group-selector [tr config]
  (let [domain-groups (<sub [::domain-groups])]
    [:div.w-full.lg:w-3-4
     [:h6.mb-2 (tr [:oidc-config/domain-groups])]
     [admin-common/member-editor {:tr                tr
                                  :groups            domain-groups
                                  :val->str          format-group-name
                                  :current-group-ids (->> config
                                                          :oidc-config/domain-groups
                                                          set)
                                  :add-title         (tr [:gis-editor/add-group])
                                  :save-evt          [::toggle-domain-group true]
                                  :remove-evt        [::toggle-domain-group false]}]]))


(def schema-errors (s/checker oidc-model/OIDCAnyConfig))


(defn oidc-config [{:keys [tr config]}]
  (let [config-in-editor (<sub [::config-in-editor])
        editing?         (or (-> config :db/id (= (:db/id config-in-editor)))
                             (-> config :db/id nil?))
        use-discovery?   (-> config-in-editor :oidc-config/discovery-uri str/blank? not)
        invalid?         (boolean (schema-errors (dissoc config-in-editor ::new-config?)))
        domain-groups    (<sub [::domain-groups])]
    [section/section
     (:oidc-config/id config)
     {:tr             tr
      :heading-button (cc/button
                        {:on-click         #(>evt [::set-config-in-editor (when-not editing?
                                                                            config)])
                         :icon             (if editing?
                                             i/remove
                                             i/pen)
                         :icon-position    :right
                         :icon-description (tr [(if editing?
                                                  :cancel
                                                  :edit)])
                         :class-name       "only-icon-button"
                         :kind             :ghost}
                        "")}
     (:oidc-config/display-name config)
     [callback-uri tr (if editing? config-in-editor config)]
     [:div.flex.flex-row.flex-1.flex-wrap.items-end.justify-between
      [:div.flex.flex-col.w-full.lg:w-2-3.lg:w-auto.max-w-xl.mb-3
       (for-frag [k mandatory-keys]
         [field tr editing? false k config config-in-editor])
       (let [k :oidc-config/discovery-uri]
         [field tr editing? false k config config-in-editor])
       (for-frag [k config-keys]
         [field tr editing? use-discovery? k config config-in-editor])
       (for-frag [k [:oidc-config/profile-scope?
                     :oidc-config/skip-iss-validation?
                     :oidc-config/check-email-verified?
                     :oidc-config/public-login?]]
         [:div.mt-3
          (cc/checkbox {:id         (str (:oidc-config/id config) "-" (name k))
                        :checked    (-> (if editing? config-in-editor config)
                                        (get k)
                                        boolean)
                        :label-text (tr [k])
                        :disabled   (not editing?)
                        :on-change  #(>evt [::set-editor-value k %])})])
       [:div.mt-3
        (let [k           :oidc-config/domain-groups
              current-ids (k config-in-editor)
              example     (p/find-first domain-groups #(not (contains? current-ids (:db/id %))))]
          (if editing?
            [:<>
             [group-selector tr config-in-editor]
             [:div.text-xs.mt-3 (tr [(if (seq current-ids)
                                       :oidc-config/domain-group-guide
                                       :oidc-config/domain-group-guide-2)]
                                    [(:group/domain example)])]]
            [labeled-text (tr [k]) (or (some->> config
                                                k
                                                (map format-group-name)
                                                sort
                                                (str/join ", "))
                                       (tr [:common/not-set]))]))]]
      (when editing?
        (if (<sub [::save-in-progress?])
          [:div.flex
           (cc/inline-loading {:description (tr [:saving])})]
          [:div.flex.gap-2
           (when (:db/id config)
             [ui/modal-confirmation-button
              {:tr                  tr
               :id                  "delete-config"
               :on-yes-click        #(>evt [::delete-config (:db/id config)])
               :modal-title         (tr [:admin.oidc-configs/delete-title])
               :trigger-button-opts {:kind       :danger
                                     :class-name "mr-8"}
               :trigger-button-text (tr [:delete])}
              (tr [:admin.oidc-configs/delete-confirmation]
                  [(:oidc-config/id config)])])
           (cc/button
             {:on-click #(>evt [::set-config-in-editor nil])
              :kind     :tertiary}
             (tr [:cancel]))
           (cc/button
             {:on-click #(>evt [::save-config config-in-editor])
              :kind     :primary
              :disabled invalid?}
             (tr [:save]))]))]]))


(defn view [_]
  (let [tr        (<sub [:tr])
        show-new? (-> (<sub [::config-in-editor]) ::new-config?)
        configs   (<sub [::entities/entities {:entity/type :entity.type/oidc-config}])]
    [:div.cloudpermit-page.admin-page
     [:div.bx--row.page-heading.justify-between
      [:h1 (tr [:view/admin.oidc-configs])]
      (cc/button
        {:icon-position    :left
         :icon             i/plus
         :icon-description ""
         :on-click         #(>evt [::set-config-in-editor default-config])}
        (tr [:admin.oidc-configs/add-new]))]
     (when show-new?
       [oidc-config {:tr     tr
                     :config default-config}])
     (for-frag [config configs]
       [oidc-config {:tr     tr
                     :config config}])]))


(def route
  {:name        :view/admin.oidc-configs
   :view        view
   :controllers [{:start (fn [_]
                           (>evt [::fetch-oidc-configs])
                           (>evt [::fetch-domain-groups]))}]})
